import { Base, withMountWhenPrefersMotion, withMountWhenInView } from '@studiometa/js-toolkit';
import { removeClass, toggleClass } from '@studiometa/js-toolkit/utils';

/**
 * useWait
 * @returns {Promise}
 */
const useWait = () => {
  let resolve = null;
  let timer = null;
  const wait = (delay) => {
    if (timer && resolve) {
      resolve();
    }
    return new Promise((r) => {
      resolve = r;
      timer = setTimeout(resolve, delay);
    });
  };
  const clear = () => {
    clearTimeout(timer);
    timer = null;
    if (resolve) {
      resolve();
    }
  };
  return {
    wait,
    clear,
  };
};

/**
 * FakeSearchInput component
 */
export default class FakeSearchInput extends withMountWhenPrefersMotion(
  withMountWhenInView(Base, { threshold: [0, 1] }),
) {
  static config = {
    name: 'FakeSearchInput',
    refs: ['placeholder', 'value', 'cursor', 'button'],
    options: {
      values: Array,
      typingDelay: {
        type: Number,
        default: 800,
      },
      typingSpeed: {
        type: Number,
        default: 100,
      },
      erasingDelay: {
        type: Number,
        default: 2400,
      },
      erasingSpeed: {
        type: Number,
        default: 50,
      },
      blinkingDelay: {
        type: Number,
        default: 800,
      },
      blinkingDuration: {
        type: Number,
        default: 500,
      },
    },
    log: true,
  };

  typing = Promise.resolve();

  isTyping = false;

  blinkTimeout = null;

  /**
   * Set value
   */
  set value(value) {
    this.stopBlinking();
    toggleClass(this.$refs.placeholder, 'opacity-0', value.length > 0);
    this.$refs.value.textContent = value;
    this.startBlinking();
  }

  /**
   * Get value
   * @returns {string}
   */
  get value() {
    return this.$refs.value.textContent;
  }

  /**
   * Blink
   */
  blink() {
    toggleClass(this.$refs.cursor, 'opacity-0');
    this.blinkTimeout = setTimeout(() => {
      this.blink();
    }, this.$options.blinkingDuration);
  }

  /**
   * Start blinking
   */
  startBlinking() {
    if (!this.isTyping) {
      return;
    }
    this.blinkTimeout = setTimeout(() => {
      this.blink();
    }, this.$options.blinkingDelay);
  }

  /**
   * Start blinking
   */
  stopBlinking() {
    clearTimeout(this.blinkTimeout);
    removeClass(this.$refs.cursor, 'opacity-0');
  }

  /**
   * Start Typing
   * @returns {Promise}
   */
  async startTyping() {
    this.isTyping = true;
    const { values, typingDelay, typingSpeed, erasingDelay, erasingSpeed } = this.$options;

    for (const [valueIndex, chars] of values.entries()) {
      if (!this.isTyping) {
        break;
      }
      await this.wait(typingDelay);
      let startAt = 0;
      if (chars.startsWith(this.value)) {
        startAt = this.value.length;
      } else {
        this.value = '';
      }
      for (const char of chars.slice(startAt)) {
        if (!this.isTyping) {
          break;
        }
        this.value += char;
        await this.wait(Math.max(erasingSpeed, Math.random() * typingSpeed));
      }
      if (!this.isTyping) {
        break;
      }
      await this.wait(erasingDelay);
      while (this.value.length > 0) {
        if (!this.isTyping) {
          break;
        }
        this.value = this.value.slice(0, -1);
        if (
          !this.value.endsWith(' ') &&
          values[valueIndex + 1 === values.length ? 0 : valueIndex + 1].startsWith(this.value)
        ) {
          break;
        } else {
          await this.wait(erasingSpeed);
        }
      }
    }
    if (!this.isTyping) {
      return Promise.resolve();
    }
    return this.startTyping();
  }

  /**
   * Mounted
   */
  async mounted() {
    const { wait, clear } = useWait();

    this.wait = wait;
    this.stop = async () => {
      if (this.isTyping) {
        this.isTyping = false;
        clear();
      }
      await this.typing;
      this.value = '';
    };

    this.stop();
    this.typing = this.startTyping();
  }

  /**
   * Destroyed
   */
  async destroyed() {
    this.stop();
    this.stopBlinking();
  }
}
